import { useCallback } from 'react';
import type {
  tChallengeKey,
  tChallengeRole,
  tHoldoutArm,
} from 'src/components/ChallengeCenter/ChallengeCenter.types';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { FIRST_WEEK_EVENTS } from 'src/hooks/analytics/firstWeek/firstWeekAnalytics.constants';

type tFirstWeekAudience = {
  /** The role from the challenge-center state, not the session's placeholder role. */
  role: tChallengeRole | null;
  holdoutArm: tHoldoutArm;
};

type tTourStarted = tFirstWeekAudience & {
  challengeKey: tChallengeKey;
};

type tTourAbandoned = tTourStarted & {
  /** The id of the step the person left from, never its index. */
  step: string;
};

type tToastViewed = {
  challengeKey: tChallengeKey;
  holdoutArm: tHoldoutArm;
};

/**
 * The five browser-side events of the "Tu primera semana" chip. Callers decide
 * when each one fires; this hook only shapes the payload.
 */
export function useFirstWeekAnalytics() {
  const { trackEvent } = useAnalytics();

  const trackChipViewed = useCallback(
    ({ role, holdoutArm }: tFirstWeekAudience) => {
      trackEvent(FIRST_WEEK_EVENTS.CHIP_VIEWED, {
        role,
        holdout_arm: holdoutArm,
      });
    },
    [trackEvent],
  );

  const trackPanelOpened = useCallback(
    ({ role, holdoutArm }: tFirstWeekAudience) => {
      trackEvent(FIRST_WEEK_EVENTS.PANEL_OPENED, {
        role,
        holdout_arm: holdoutArm,
      });
    },
    [trackEvent],
  );

  const trackTourStarted = useCallback(
    ({ challengeKey, role, holdoutArm }: tTourStarted) => {
      trackEvent(FIRST_WEEK_EVENTS.TOUR_STARTED, {
        challenge_key: challengeKey,
        role,
        holdout_arm: holdoutArm,
      });
    },
    [trackEvent],
  );

  const trackTourAbandoned = useCallback(
    ({ challengeKey, step, role, holdoutArm }: tTourAbandoned) => {
      trackEvent(FIRST_WEEK_EVENTS.TOUR_ABANDONED, {
        challenge_key: challengeKey,
        step,
        role,
        holdout_arm: holdoutArm,
      });
    },
    [trackEvent],
  );

  const trackToastViewed = useCallback(
    ({ challengeKey, holdoutArm }: tToastViewed) => {
      trackEvent(FIRST_WEEK_EVENTS.TOAST_VIEWED, {
        challenge_key: challengeKey,
        holdout_arm: holdoutArm,
      });
    },
    [trackEvent],
  );

  return {
    trackChipViewed,
    trackPanelOpened,
    trackTourStarted,
    trackTourAbandoned,
    trackToastViewed,
  };
}
